import { del, delMany, get, getMany, keys, setMany } from 'idb-keyval'
import type { StoredFont, StoredFontSummary } from '@/types'
import { FONT_STORAGE_KEY } from '@/constants'
import {
  FONT_RECORD_VERSION,
  FontRegistry,
  decodeLegacyFontBase64,
  readIndexedFontSummary,
  type BinaryFontRecord,
  type FontFaceResource,
  type FontFormat,
  type FontRecordStore,
  type FontRuntime,
} from '@/v2/fonts/FontRegistry'

const LEGACY_STORAGE_PREFIX = `${FONT_STORAGE_KEY}:`
const DATA_STORAGE_PREFIX = `${FONT_STORAGE_KEY}:data:`
const METADATA_STORAGE_PREFIX = `${FONT_STORAGE_KEY}:metadata:`

function legacyStorageKey(name: string): string {
  return `${LEGACY_STORAGE_PREFIX}${name}`
}

function dataStorageKey(name: string): string {
  return `${DATA_STORAGE_PREFIX}${name}`
}

function metadataStorageKey(name: string): string {
  return `${METADATA_STORAGE_PREFIX}${name}`
}

let metadataOperationTail: Promise<void> = Promise.resolve()

function withMetadataLock<TResult>(operation: () => Promise<TResult>): Promise<TResult> {
  const result = metadataOperationTail.catch(() => undefined).then(operation)
  metadataOperationTail = result.then(
    () => undefined,
    () => undefined,
  )
  return result
}

function isLegacyInvalidMarker(value: unknown, legacyKey: string): boolean {
  try {
    if (typeof value !== 'object' || value === null) return false
    const marker = value as Record<string, unknown>
    return marker.invalid === true && marker.legacyKey === legacyKey
  } catch {
    return false
  }
}

const fontStore: FontRecordStore = {
  async get(name) {
    const current = await get<unknown>(dataStorageKey(name))
    return current ?? get<unknown>(legacyStorageKey(name))
  },
  set(name, value) {
    if (value.name !== name) {
      return Promise.reject(new Error('Font storage key does not match record name'))
    }
    return withMetadataLock(async () => {
      await setMany([
        [dataStorageKey(name), value],
        [metadataStorageKey(name), summary(value)],
      ])
      // The legacy payload remains recoverable until the V2 data+metadata commit succeeds.
      try {
        await del(legacyStorageKey(name))
      } catch (error) {
        console.warn(`Could not remove migrated legacy font ${name}:`, error)
      }
    })
  },
  delete(name) {
    return withMetadataLock(() => delMany([
      dataStorageKey(name),
      metadataStorageKey(name),
      legacyStorageKey(name),
    ]))
  },
  async listMetadata() {
    const snapshot = await withMetadataLock(async () => {
      const allKeys = await keys()
      const metadataKeys = allKeys.filter(
        (key): key is string => typeof key === 'string' && key.startsWith(METADATA_STORAGE_PREFIX)
      )
      const rawMetadata = await getMany<unknown>(metadataKeys)
      const metadata: StoredFontSummary[] = []
      const metadataNames = new Set<string>()
      rawMetadata.forEach((value, index) => {
        const indexedName = (metadataKeys[index] ?? '').slice(METADATA_STORAGE_PREFIX.length)
        const candidate = readIndexedFontSummary(indexedName, value)
        if (candidate !== null) {
          metadata.push(candidate)
          metadataNames.add(indexedName)
        } else if (isLegacyInvalidMarker(value, legacyStorageKey(indexedName))) {
          metadataNames.add(indexedName)
        }
      })

      // One-time discovery for the old single-record layout. Only metadata is
      // cached here; binary conversion and payload migration remain lazy.
      const legacyKeys = allKeys.filter((key): key is string => {
        if (typeof key !== 'string'
          || !key.startsWith(LEGACY_STORAGE_PREFIX)
          || key.startsWith(DATA_STORAGE_PREFIX)
          || key.startsWith(METADATA_STORAGE_PREFIX)) {
          return false
        }
        return !metadataNames.has(key.slice(LEGACY_STORAGE_PREFIX.length))
      })
      return { metadata, legacyKeys }
    })

    const discovered: StoredFontSummary[] = []
    for (const legacyKey of snapshot.legacyKeys) {
      const candidate = await withMetadataLock(async () => {
        const indexedName = legacyKey.slice(LEGACY_STORAGE_PREFIX.length)
        const currentMetadata = await get<unknown>(metadataStorageKey(indexedName))
        const currentSummary = readIndexedFontSummary(indexedName, currentMetadata)
        if (currentSummary !== null) return currentSummary
        if (isLegacyInvalidMarker(currentMetadata, legacyKey)) return null

        // Hydrate at most one legacy payload while holding one short mutation slot.
        const legacyValue = await get<unknown>(legacyKey)
        if (legacyValue === undefined) return null
        const legacySummary = readIndexedFontSummary(indexedName, legacyValue)
        const metadataValue = legacySummary ?? { invalid: true, legacyKey }
        await setMany([[metadataStorageKey(indexedName), metadataValue]])
        return legacySummary
      })
      if (candidate !== null) discovered.push(candidate)
    }
    return [...snapshot.metadata, ...discovered]
  },
}

class BrowserFontFaceResource implements FontFaceResource {
  constructor(
    readonly native: FontFace | null,
    readonly fallbackStyle: HTMLStyleElement | null,
  ) {}

  async load(): Promise<void> {
    if (this.native !== null) await this.native.load()
  }
}

function escapeCssString(value: string): string {
  return value.replace(/["\\\n\r\f]/gu, (character) => `\\${character}`)
}

const browserFontRuntime: FontRuntime<BrowserFontFaceResource> = {
  createObjectURL: (data) => URL.createObjectURL(data),
  revokeObjectURL: (url) => { URL.revokeObjectURL(url) },
  createFontFace(family, source) {
    if (typeof FontFace !== 'undefined') {
      return new BrowserFontFaceResource(new FontFace(family, source), null)
    }

    const style = document.createElement('style')
    style.id = `font-face-${family.replace(/\s+/gu, '-')}`
    style.textContent = `@font-face { font-family: "${escapeCssString(family)}"; src: ${source}; font-weight: normal; font-style: normal; }`
    return new BrowserFontFaceResource(null, style)
  },
  addFontFace(resource) {
    if (resource.native !== null) {
      document.fonts.add(resource.native)
    } else if (resource.fallbackStyle !== null) {
      document.getElementById(resource.fallbackStyle.id)?.remove()
      document.head.appendChild(resource.fallbackStyle)
    }
  },
  deleteFontFace(resource) {
    if (resource.native !== null) document.fonts.delete(resource.native)
    resource.fallbackStyle?.remove()
  },
}

const fontRegistry = new FontRegistry(fontStore, browserFontRuntime)

function decodeBase64(base64Data: string): ArrayBuffer {
  const decoded = decodeLegacyFontBase64(base64Data)
  if (decoded === null) throw new TypeError('Invalid or oversized base64 font data')
  return decoded
}

async function binaryToBase64(data: Blob | ArrayBuffer): Promise<string> {
  const buffer = data instanceof Blob ? await data.arrayBuffer() : data
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const end = Math.min(bytes.length, offset + chunkSize)
    for (let index = offset; index < end; index += 1) {
      binary += String.fromCharCode(bytes[index] ?? 0)
    }
  }
  return globalThis.btoa(binary)
}

function mimeType(format: FontFormat): string {
  switch (format) {
    case 'otf': return 'font/otf'
    case 'woff': return 'font/woff'
    case 'woff2': return 'font/woff2'
    default: return 'font/ttf'
  }
}

function summary(record: BinaryFontRecord): StoredFontSummary {
  return {
    name: record.name,
    format: record.format,
    dateAdded: record.dateAdded,
  }
}

/** Persist a new record as binary data rather than inflating it to base64. */
export function saveFontBinary(input: {
  readonly name: string
  readonly format: FontFormat
  readonly dateAdded: number
  readonly data: Blob | ArrayBuffer
}): Promise<void> {
  return fontRegistry.save({ version: FONT_RECORD_VERSION, ...input })
}

/** Legacy compatibility helper. New editor code should call saveFontBinary. */
export function saveFont(font: StoredFont): Promise<void> {
  return saveFontBinary({
    name: font.name,
    format: font.format,
    dateAdded: font.dateAdded,
    data: decodeBase64(font.base64Data),
  })
}

/** Metadata-only discovery used after the editor shell becomes ready. */
export async function loadFontSummaries(): Promise<StoredFontSummary[]> {
  return fontRegistry.list()
}

/** Legacy/export compatibility helper that hydrates binary bodies on demand. */
export async function loadFonts(): Promise<StoredFont[]> {
  try {
    const summaries = await fontRegistry.list()
    const fonts = await Promise.all(summaries.map(async (font) => {
      const record = await fontRegistry.load(font.name)
      if (record === null) return null
      return { ...summary(record), base64Data: await binaryToBase64(record.data) }
    }))
    return fonts.filter((font): font is StoredFont => font !== null)
  } catch (error) {
    console.error('Failed to load fonts from IndexedDB:', error)
    return []
  }
}

export function deleteFont(name: string): Promise<void> {
  return fontRegistry.delete(name)
}

export async function getFont(name: string): Promise<StoredFont | undefined> {
  const record = await fontRegistry.load(name)
  if (record === null) return undefined
  return { ...summary(record), base64Data: await binaryToBase64(record.data) }
}

export async function fontExists(name: string): Promise<boolean> {
  return (await fontRegistry.load(name)) !== null
}

/** Register one selected font. Concurrent callers share the same load. */
export function registerStoredFont(name: string): Promise<boolean> {
  return fontRegistry.register(name)
}

/** Worker-safe source for a selected font after its face has finished loading. */
export function getRegisteredFontSource(name: string): string | null {
  return fontRegistry.getRegisteredSource(name)
}

/** Legacy compatibility helper; persists the supplied record before loading it. */
export async function registerFont(font: StoredFont): Promise<void> {
  await saveFont(font)
  await registerStoredFont(font.name)
}

export function unregisterFont(name: string): void {
  void fontRegistry.unregister(name)
}

export function releaseRegisteredFonts(): void {
  void fontRegistry.releaseAll()
}

/** Kept for callers outside the editor; the editor itself registers lazily. */
export async function registerAllStoredFonts(): Promise<void> {
  const fonts = await loadFontSummaries()
  for (const font of fonts) {
    try {
      await registerStoredFont(font.name)
    } catch (error) {
      console.warn(`Could not register font ${font.name}:`, error)
    }
  }
}

export async function getFontDataUrl(name: string): Promise<string | null> {
  const font = await getFont(name)
  return font === undefined
    ? null
    : `data:${mimeType(font.format)};base64,${font.base64Data}`
}

export async function getFontBase64(name: string): Promise<string | null> {
  return (await getFont(name))?.base64Data ?? null
}
